/*============================================================================*/

/**
 * Animation functions
 *
 * Functions that trigger or control animations. CSS handles the
 * visual transitions; these functions only initiate or update
 * animation-related states.
 */

/*============================================================================*/


/**
 * Sets up a 3D parallax hover effect on specified elements.
 * Adds the necessary classes for styling automatically.
 * 
 * @param {string} containerSelector - Selector for the main container elements.
 * @param {string} subContainerSelector - Selector for the inner rotatable container elements.
 * @param {string} mediaContainerSelector - Selector for the media wrapper (reflection/shadow).
 * @param {string} mediaSelector - Selector for the media element itself.
 * @param {number} rotationX - Maximum rotation in X-axis (degrees).
 * @param {number} rotationY - Maximum rotation in Y-axis (degrees).
 * @param {object} [reflectionOptions] - Optional reflection settings: degreeX, degreeY, opacity
 * @param {object} [shadowOptions] - Optional shadow settings: offset, blur, opacity
 */
function applyCardParallaxEffect({
    containerSelector = '.cardContainer',
    subContainerSelector = '.cardSubContainer',
    mediaContainerSelector = '.cardMediaContainer',
    mediaSelector = '.cardMedia',
    rotationX = 20,
    rotationY = 20,
    reflectionOptions,
    shadowOptions
} = {})
{
    if (window.screen.width <= 1000) return;

    jQuery(containerSelector).addClass('parallaxEffectContainer');
    jQuery(subContainerSelector).addClass('parallaxEffectContent');
    jQuery(mediaContainerSelector).addClass('parallaxEffectMediaContainer');
    jQuery(mediaSelector).addClass('parallaxEffectMedia');

    if (reflectionOptions)
    {
        jQuery(mediaContainerSelector).css(
            '--reflection-opacity',
            reflectionOptions.opacity || 0
        );
    }

    let requestId = null;

    const updateTransform = (event, container) => {
        const width = container.width();
        const height = container.height();

        const mouseX = event.pageX - container.offset().left;
        const mouseY = event.pageY - container.offset().top;

        const rotateX = ((mouseY / height) - 0.5) * rotationX;
        const rotateY = ((mouseX / width) - 0.5) * rotationY;

        container.css(
            'transform',
            `perspective(${width / 0.5}px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`
        );

        if (reflectionOptions)
        {
            const reflectionX = ((mouseX / width) - 0.5) * (reflectionOptions.degreeX || 0);
            const reflectionY = ((mouseY / height) - 0.5) * (reflectionOptions.degreeY || 0);

            container.find(mediaContainerSelector).css({
                '--reflection-x': `${reflectionX}%`,
                '--reflection-y': `${reflectionY}%`
            });
        }

        if (shadowOptions)
        {
            const shadowX = ((mouseX / width) - 0.5) * (shadowOptions.offset || 0);
            const shadowY = ((mouseY / height) - 0.5) * (shadowOptions.offset || 0);
            const blur = shadowOptions.blur || 0;
            const opacity = shadowOptions.opacity || 0;
            container.find(mediaContainerSelector).css(
                'box-shadow',
                `${shadowX}px ${shadowY}px ${blur}px rgba(0,0,0,${opacity})`
            );
        }
    };

    const handleMouseMove = (event) => {
        const container = jQuery(event.currentTarget);
        if (requestId) cancelAnimationFrame(requestId);
        requestId = requestAnimationFrame(() => updateTransform(event, container));
    };

    const handleMouseLeave = (event) => {
        const container = jQuery(event.currentTarget);
        container.css({ 'transform': 'perspective(1000px) rotateX(0deg) rotateY(0deg)' });
        container.find(mediaContainerSelector).css({
            '--reflection-x': '0%',
            '--reflection-y': '0%',
            'box-shadow': '0px 0px 0px rgba(0,0,0,0)'
        });
        if (requestId) cancelAnimationFrame(requestId);
        requestId = null;
    };

    jQuery(document).on('mousemove', subContainerSelector, handleMouseMove);
    jQuery(document).on('mouseleave', subContainerSelector, handleMouseLeave);
}

/**
 * Animates a single element out, executes a callback to update its content, 
 * and then animates it back in.
 * Handles class cleanup to prevent conflicts.
 *
 * @param {Object} params - Configuration object.
 * @param {jQuery} params.element - The single element wrapper to animate.
 * @param {string} params.animationIn - CSS class for the entry animation.
 * @param {string} params.animationOut - CSS class for the exit animation.
 * @param {Function} params.onUpdate - Callback function executed mid-animation to update data.
 * @param {number} [params.duration=150] - Time in ms to wait before swapping content.
 */
function applyAnimationOnUpdate({ element, animationIn, animationOut, onUpdate, duration = 150 })
{
    if (!element.length)
    {
        if (typeof onUpdate === 'function') onUpdate();
        return;
    }

    element.removeClass('slideInLeft slideInRight slideOutLeft slideOutRight');

    element.addClass(animationOut);

    setTimeout(() => 
    {
        if (typeof onUpdate === 'function')
        {
            onUpdate();
        }

        element
            .removeClass(animationOut)
            .addClass(animationIn)
            .one('animationend', () => 
            {
                element.removeClass(animationIn);
            });
    }, duration);
}

/**
 * Apply custom transitions between two sections using CSS custom animations
 *
 * @param {Object} options
 * @param {jQuery|Element} options.current - The section currently visible on screen (jQuery element).
 * @param {jQuery|Element} options.next - The section to transition into view (jQuery element).
 * @param {string} options.animationOut - CSS class defining the exit animation for the current section.
 * @param {string} options.animationIn - CSS class defining the entry animation for the next section.
 * @param {Function} [options.onComplete] - Optional callback executed after the animation finishes.
 */
function applyCustomTransition({ current, next, animationOut, animationIn, onComplete } = {})
{
    current
        .removeClass('hidden slideInLeft slideInRight slideOutLeft slideOutRight')
        .addClass(animationOut);

    setTimeout(() => {
        current.addClass('hidden').removeClass(animationOut);

        next
            .removeClass('hidden slideInLeft slideInRight slideOutLeft slideOutRight')
            .addClass(animationIn)
            .one('animationend', () => {
                next.removeClass(animationIn);

                if (typeof onComplete === 'function')
                {
                    onComplete();
                }
            });

    }, 150);
}

/**
 * Smoothly shows an element, handling tricky backgrounds or non-standard blocks.
 *
 * @param {Object} data - Configuration object
 * @param {jQuery} data.element - jQuery element to fade in
 * @param {number} [data.duration=250] - Duration of the animation in milliseconds
 * @param {boolean} [data.focusInput=true] - Whether to focus the input inside the element
 */
function manualFadeInElement(data)
{
    const { element, duration = 250, focusInput = true } = data;

    element.stop(true, true)
        .removeClass('hidden')
        .css({ opacity: 0, display: 'block' })
        .animate({ opacity: 1 }, duration, function()
        {
            if (focusInput) element.find('input').focus();
        });
}

/**
 * Smoothly hides an element, handling tricky backgrounds or non-standard blocks.
 *
 * @param {Object} data - Configuration object
 * @param {jQuery} data.element - jQuery element to fade out
 * @param {number} [data.duration=250] - Duration of the animation in milliseconds
 */
function manualFadeOutElement({ element, duration = 250 })
{
    element.stop(true, true).animate({ opacity: 0 }, duration, function()
    {
        setTimeout(() =>
        {
            element.addClass('hidden');
        }, duration + 50);
    });
}

/**
 * Scrolls a scrollable container horizontally to center a specific target element within its viewport.
 * Useful for carousels, tabs, or horizontal lists.
 *
 * @param {Object} params - Configuration object.
 * @param {jQuery} params.container - The scrollable container element.
 * @param {jQuery} params.target - The child element to be centered.
 * @param {number} [params.duration=300] - Animation duration in ms.
 * @param {number} [params.offsetAdjustment=0] - Optional extra pixel offset (e.g., for margins).
 */
function scrollContainerToCenterTarget({ container, target, duration = 300, offsetAdjustment = 0 })
{
    if (!container.length || !target.length)
    {
        return;
    }

    const containerRect = container[0].getBoundingClientRect();
    const targetRect = target[0].getBoundingClientRect();
    
    const currentScroll = container.scrollLeft();
    const centerOffset = targetRect.left - containerRect.left - (containerRect.width / 2) + (targetRect.width / 2);
    
    container.stop(true, true).animate({ 
        scrollLeft: currentScroll + centerOffset + offsetAdjustment
    }, duration);
}